import {Injectable} from "@angular/core";
import {Router} from "@angular/router";
import {Subject, Subscription, switchMap, takeUntil, timer} from "rxjs";
import {SFTPAPIService} from "@app/services/sftp-api/sftp-api.service";
import {AuthorizationService} from "./authorization.service";
import {SFTPMainPages} from "@app/modules/models";

@Injectable()
export class SFTPConnectionService {
  private _isUserConnected: boolean;
  private _connectionCheckSubscription: Subscription = null;
  private _destroy$: Subject<void>;
  private readonly CONNECTION_CHECK_INTERVAL = 1000 * 20;

  constructor(
    private sftpAPIService: SFTPAPIService,
    private authorizationService: AuthorizationService,
    private router: Router
  ) {}

  get isUserConnected(): boolean {
    return this._isUserConnected;
  }

  initialize(): void {
    this._destroy$ = new Subject<void>();

    this.sftpAPIService
      .isConnected()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          if (window.location.pathname.startsWith(`/${SFTPMainPages.login}`)) {
            this.router.navigate([`/${SFTPMainPages.browse}`]).catch(console.error);
          }

          this.startConnectionCheck();
        },
        error: () => {
          window.location.replace(`/${SFTPMainPages.login}`);
        },
      });
  }

  destroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._destroy$ = null;

    this.stopConnectionCheck();
  }

  disableConnection(): void {
    this.stopConnectionCheck();
  }

  private startConnectionCheck(): void {
    this.stopConnectionCheck();

    this._connectionCheckSubscription = timer(this.CONNECTION_CHECK_INTERVAL, this.CONNECTION_CHECK_INTERVAL)
      .pipe(
        takeUntil(this._destroy$),
        switchMap(() => this.sftpAPIService.isConnected().pipe(takeUntil(this._destroy$)))
      )
      .subscribe({
        next: () => {
          if (window.location.pathname.startsWith(`/${SFTPMainPages.login}`)) {
            this.stopConnectionCheck();
          }
        },
        error: () => {
          this.stopConnectionCheck();
          this.router.navigate([`/${SFTPMainPages.login}`]).catch(console.error);
        },
      });
  }

  private stopConnectionCheck(): void {
    if (this._connectionCheckSubscription) {
      this._connectionCheckSubscription.unsubscribe();
      this._connectionCheckSubscription = null;
    }
  }
}
